import { gsap } from '../lib/gsap';
import { prefersReducedMotion } from '../lib/lenis';

interface PageLoaderOptions {
  /** Fires at the instant the loader starts its lift-away. */
  onDissolveStart?: () => void;
}

const MAX_DURATION_MS = 4500;

/**
 * Full-screen loader. The teamix sharp mark draws its own outline
 * (stroke-dashoffset), then fills with accent "liquid" — a sine-wave band
 * rising inside an SVG mask, its surface rippling sideways as it climbs —
 * while a mini equalizer pulses below. Then everything flies upward and the
 * overlay lifts away like a curtain. The hero entrance fires during the lift
 * so the motions overlap and read as one continuous reveal.
 *
 * Safety net: a hard timer removes the loader even if the GSAP timeline ever
 * fails to complete.
 */
export function initPageLoader(opts: PageLoaderOptions = {}): Promise<void> {
  return new Promise((resolve) => {
    const loader = document.querySelector<HTMLElement>('[data-loader]');
    if (!loader) {
      opts.onDissolveStart?.();
      resolve();
      return;
    }

    let finished = false;
    let dissolveStarted = false;

    const fireDissolveStart = () => {
      if (dissolveStarted) return;
      dissolveStarted = true;
      try { opts.onDissolveStart?.(); } catch (e) { console.error('[loader] onDissolveStart', e); }
    };

    const finish = () => {
      if (finished) return;
      finished = true;
      fireDissolveStart(); // ensure consumers got the signal at least once
      try { loader.parentNode?.removeChild(loader); } catch {}
      resolve();
    };

    // Hard safety timer — removes the overlay even if everything else fails.
    const timeout = window.setTimeout(finish, MAX_DURATION_MS);

    try {
      const mark    = loader.querySelector<HTMLElement>('[data-loader-mark]');
      const outline = loader.querySelector<SVGPathElement>('[data-loader-outline]');
      const waveY   = loader.querySelector<SVGGElement>('[data-loader-wave-y]');
      const waveX   = loader.querySelector<SVGGElement>('[data-loader-wave-x]');
      const glow    = loader.querySelector<HTMLElement>('[data-loader-glow]');
      const eq      = loader.querySelector<HTMLElement>('[data-loader-eq]');

      if (prefersReducedMotion()) {
        if (waveY) gsap.set(waveY, { y: -4 });
        gsap.delayedCall(0.3, () => {
          fireDissolveStart();
          gsap.to(loader, {
            autoAlpha: 0,
            duration: 0.4,
            ease: 'power2.out',
            onComplete: () => { window.clearTimeout(timeout); finish(); },
          });
        });
        return;
      }

      const tl = gsap.timeline({
        onComplete: () => { window.clearTimeout(timeout); finish(); },
      });

      // ── 1. The outline draws itself around the glyph ─────────────────────
      if (outline) {
        const len = outline.getTotalLength();
        gsap.set(outline, { strokeDasharray: len, strokeDashoffset: len });
        tl.to(outline, { strokeDashoffset: 0, duration: 1.0, ease: 'power1.inOut' }, 0);
      }

      // ── 2. Liquid rises inside it, surface rippling sideways ─────────────
      // waveY lifts the masked wave band from below the glyph to above it;
      // waveX loops one full wave period so the surface never sits still.
      if (waveY) {
        gsap.set(waveY, { y: 57 });
        tl.to(waveY, { y: -4, duration: 1.4, ease: 'power1.inOut' }, 0.45);
      }
      if (waveX) {
        gsap.to(waveX, { x: -26, duration: 0.9, ease: 'none', repeat: -1 });
      }
      if (glow) {
        tl.fromTo(
          glow,
          { opacity: 0, scale: 0.6 },
          { opacity: 1, scale: 1, duration: 1.4, ease: 'power2.out' },
          0.45
        );
      }

      // A breath at full level — one clean beat of the complete mark.
      tl.to({}, { duration: 0.25 });
      tl.call(fireDissolveStart);

      // ── Exit: everything flies to the sky, the curtain lifts ─────────────
      if (mark) {
        tl.to(mark, {
          y: -140,
          scale: 1.08,
          autoAlpha: 0,
          filter: 'blur(10px)',
          duration: 0.7,
          ease: 'power3.in',
        }, '<');
      }
      if (eq) {
        tl.to(eq.children, {
          y: -90,
          autoAlpha: 0,
          duration: 0.55,
          ease: 'power3.in',
          stagger: 0.04,
        }, '<');
      }
      tl.to(
        loader,
        { yPercent: -100, duration: 0.9, ease: 'power4.inOut' },
        '<+=0.25'
      );
    } catch (e) {
      console.error('[loader] timeline build failed', e);
      // Force finish so the page is usable.
      window.clearTimeout(timeout);
      finish();
    }
  });
}
